import React, { useState, useEffect } from 'react';
import { Alert, Form, Col, Row, Table, Button, Modal, Spinner, Badge, Card, FilterGrid } from '@/components/ui';
import ReportParametersCard from '../components/ReportParametersCard';
import { areasService, ServiceArea } from '../../../services/areasService';
import activityService from '../../../services/activityService';
import { Activity } from '../../../types/Activity';
import reportService from '../../../services/reportService';
import Toast from '../../../components/Toast';
import InfoTooltip from '../../../components/InfoTooltip';

interface QBSQuantifiedValueReportViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

interface MonthValueBreakdown {
  actual: number;
  planned: number;
  total: number;
}

interface ActivityRow {
  activity: Activity;
  unitOfMeasure: string;
  monthValues: MonthValueBreakdown[];
  actualValueToDate: number;
  plannedValueToDate: number;
  totalValueToDate: number;
  totalAccomplishment: number;
  totalPlannedAccomplishment: number;
}

interface ReportDiagnostics {
  tasksSkippedNoDate: number;
  tasksSkippedOutsideMonths: number;
}

interface ReportSummary {
  annualTarget: number | null;
  totalActualValue: number;
  totalPlannedValue: number;
  totalValue: number;
  projectedPercentOfTarget: number | null;
}

interface DrillThroughTask {
  taskId: number;
  taskNumber: string | null;
  taskName: string | null;
  status: string;
  value: number;
  accomplishment: number | null;
  date: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  FINALIZED: 'Finalized',
  CLOSED: 'Closed',
  INTERNAL_REVIEW: 'Internal Review',
  PLANNED: 'Planned',
  ACTIVE: 'Active',
  ACTIVE_MARKED_AS_COMPLETE: 'Active (Marked Complete)',
};

const STATUS_VARIANT: Record<string, string> = {
  FINALIZED: 'success',
  CLOSED: 'secondary',
  INTERNAL_REVIEW: 'info',
  PLANNED: 'primary',
  ACTIVE: 'warning',
  ACTIVE_MARKED_AS_COMPLETE: 'warning',
};

const QBSQuantifiedValueReportView: React.FC<QBSQuantifiedValueReportViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    quantifiedYear: '',
    startDate: '',
    endDate: '',
    showActual: true,
    showPlanned: true
  });

  const effectiveViewMode: 'actual' | 'planned' | 'both' =
    filters.showActual && filters.showPlanned ? 'both'
      : filters.showPlanned ? 'planned'
        : 'actual';

  const [qbsPlanYearOptions, setQbsPlanYearOptions] = useState<number[]>([]);
  const [loadingQbsPlanYears, setLoadingQbsPlanYears] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activityRows, setActivityRows] = useState<ActivityRow[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [monthHeaders, setMonthHeaders] = useState<Array<{ label: string; dateRange: string }>>([]);
  const [diagnostics, setDiagnostics] = useState<ReportDiagnostics | null>(null);
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);

  const [drillThrough, setDrillThrough] = useState<{
    show: boolean;
    activityId: number | null;
    activityLabel: string;
    monthLabel: string;
    monthIndex: number | null;
    tasks: DrillThroughTask[];
    loading: boolean;
    error: string | null;
  }>({
    show: false,
    activityId: null,
    activityLabel: '',
    monthLabel: '',
    monthIndex: null,
    tasks: [],
    loading: false,
    error: null
  });

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (defaultServiceAreaId && !filters.serviceArea) {
      setFilters(prev => ({ ...prev, serviceArea: defaultServiceAreaId }));
    }
  }, [defaultServiceAreaId]);

  useEffect(() => {
    if (!filters.serviceArea) {
      setQbsPlanYearOptions([]);
      setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
      return;
    }
    let cancelled = false;
    const serviceAreaId = parseInt(filters.serviceArea, 10);
    if (Number.isNaN(serviceAreaId)) {
      setQbsPlanYearOptions([]);
      return;
    }
    setLoadingQbsPlanYears(true);
    areasService
      .getQuantifiedPlans(serviceAreaId)
      .then((plans) => {
        if (cancelled) return;
        const eligiblePlans = plans.filter((p) => p.status === 'Open' || p.status === 'Closed');
        const years = Array.from(new Set(eligiblePlans.map((p) => p.year))).sort((a, b) => b - a);
        setQbsPlanYearOptions(years);
        const openYears = plans.filter((p) => p.status === 'Open').map((p) => p.year);
        let defaultYear: number | null = null;
        if (years.includes(currentYear)) {
          defaultYear = currentYear;
        } else if (openYears.length > 0) {
          defaultYear = Math.max(...openYears);
        } else if (years.length > 0) {
          defaultYear = Math.max(...years);
        }
        setFilters((prev) => ({
          ...prev,
          quantifiedYear: defaultYear != null ? String(defaultYear) : ''
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setQbsPlanYearOptions([]);
          setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingQbsPlanYears(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.serviceArea]);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!filters.serviceArea || !filters.quantifiedYear) {
        setActivities([]);
        setActivityRows([]);
        setSummary(null);
        return;
      }

      setLoadingActivities(true);
      try {
        const filtered = await activityService.getQuantifiedActivitiesByServiceAreaAndYear(
          Number(filters.serviceArea),
          Number(filters.quantifiedYear)
        );
        setActivities(filtered);
        setActivityRows([]);
        setSummary(null);
      } catch (error) {
        console.error('Error fetching activities:', error);
        setErrorMessage('Failed to load activities. Please try again.');
        setActivities([]);
        setActivityRows([]);
        setSummary(null);
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [filters.serviceArea, filters.quantifiedYear]);

  const handleGeneratePreview = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }
    if (!filters.showActual && !filters.showPlanned) {
      setErrorMessage('Please select at least one of Actual or Planned under Show Values');
      return;
    }

    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const data = await reportService.getQBSQuantifiedValue({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        viewMode: effectiveViewMode
      });

      setMonthHeaders(data.monthHeaders || []);
      setDiagnostics(data.diagnostics || null);
      setSummary(data.summary || null);

      const rows: ActivityRow[] = data.activityRows.map(row => {
        const activity = activities.find(a => a.id === row.activityId) || {
          id: row.activityId,
          activityNumber: row.activityNumber,
          activityName: row.activityName,
          unitOfMeasure: row.unitOfMeasure || ''
        } as Activity;

        return {
          activity,
          unitOfMeasure: row.unitOfMeasure || activity.unitOfMeasure || '',
          monthValues: row.monthValues,
          actualValueToDate: row.actualValueToDate,
          plannedValueToDate: row.plannedValueToDate,
          totalValueToDate: row.totalValueToDate,
          totalAccomplishment: row.totalAccomplishment || 0,
          totalPlannedAccomplishment: row.totalPlannedAccomplishment || 0
        };
      });

      setActivityRows(rows);
    } catch (error: any) {
      console.error('Error generating QBS Quantified Value report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to generate QBS Quantified Value report. Please try again later.');
      setActivityRows([]);
      setDiagnostics(null);
      setSummary(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDrillThrough = async (row: ActivityRow, monthIndex: number, monthLabel: string) => {
    if (!filters.serviceArea || !filters.quantifiedYear) return;
    const activityId = row.activity.id;
    if (!activityId) return;

    const activityLabel = row.activity.activityNumber && row.activity.activityName
      ? `(#${row.activity.activityNumber}) ${row.activity.activityName}`
      : row.activity.activityName || row.activity.activityNumber || 'Unknown Activity';

    setDrillThrough({
      show: true,
      activityId,
      activityLabel,
      monthLabel,
      monthIndex,
      tasks: [],
      loading: true,
      error: null
    });

    try {
      const tasks = await reportService.getQBSQuantifiedValueDrillThrough({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        activityId,
        monthIndex,
        viewMode: effectiveViewMode,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      setDrillThrough(prev => ({ ...prev, tasks, loading: false }));
    } catch (err: any) {
      setDrillThrough(prev => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.message || 'Failed to load tasks. Please try again.'
      }));
    }
  };

  const closeDrillThrough = () => {
    setDrillThrough(prev => ({ ...prev, show: false }));
  };

  const formatDollar = (value: number | string, showZero = false): string => {
    if (value === '' || value === null || value === undefined) return '-';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return '-';
    if (num === 0 && !showZero) return '-';
    return '$' + Math.round(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatQuantity = (value: number | string): string => {
    if (value === '' || value === null || value === undefined) return '-';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num) || num === 0) return '-';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const displayedMonthValue = (cell: MonthValueBreakdown): number => {
    if (effectiveViewMode === 'actual') return cell.actual;
    if (effectiveViewMode === 'planned') return cell.planned;
    return cell.total;
  };

  const truncateActivityName = (text: string, maxLength: number = 50): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleExport = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }

    setLoadingExport(true);
    setErrorMessage(null);
    try {
      await reportService.downloadQBSQuantifiedValue({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        viewMode: effectiveViewMode
      });
      showSuccessToast('QBS Quantified Value report exported successfully!');
    } catch (error: any) {
      console.error('Error exporting QBS Quantified Value report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to export QBS Quantified Value report. Please try again later.');
    } finally {
      setLoadingExport(false);
    }
  };

  const viewModeLabel =
    effectiveViewMode === 'both' ? 'Actual + Planned'
      : effectiveViewMode === 'planned' ? 'Planned'
        : 'Actual';

  const renderMonthTooltip = (cell: MonthValueBreakdown) => (
    <>
      Actual: {formatDollar(cell.actual, true)}
      <br />
      Planned: {formatDollar(cell.planned, true)}
      <br />
      Total: {formatDollar(cell.total, true)}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'auto' }}>
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        {diagnostics && diagnostics.tasksSkippedNoDate > 0 && (
          <Alert variant="warning" className="tw-mb-3 tw-py-2">
            {diagnostics.tasksSkippedNoDate} task{diagnostics.tasksSkippedNoDate === 1 ? '' : 's'} excluded: no completion or planned date.
            {diagnostics.tasksSkippedOutsideMonths > 0 && (
              <> {diagnostics.tasksSkippedOutsideMonths} task{diagnostics.tasksSkippedOutsideMonths === 1 ? '' : 's'} excluded: date outside M1-M12 range.</>
            )}
          </Alert>
        )}

        <ReportParametersCard
          onGenerate={handleGeneratePreview}
          onExport={() => {}}
          loadingGenerate={loadingPreview}
          loadingExport={false}
          disableExport={true}
          generateButtonText="Run Report"
          hideExportButton={true}
          defaultCollapsed={false}
          onCollapseChange={setIsFiltersCollapsed}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <Form.Select
                size="sm"
                value={filters.serviceArea}
                onChange={(e) => setFilters(prev => ({ ...prev, serviceArea: e.target.value }))}
                disabled={loadingServiceAreas}
              >
                <option value="">All Service Areas</option>
                {serviceAreas.map((area) => (
                  <option key={area.id} value={area.id.toString()}>
                    {area.name || area.code || 'Unknown'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Start Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">End Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Quantified Year</Form.Label>
              <Form.Select
                size="sm"
                value={filters.quantifiedYear}
                onChange={(e) => setFilters(prev => ({ ...prev, quantifiedYear: e.target.value }))}
                disabled={!filters.serviceArea || loadingQbsPlanYears || qbsPlanYearOptions.length === 0}
              >
                <option value="">
                  {!filters.serviceArea
                    ? 'Select service area first'
                    : loadingQbsPlanYears
                      ? 'Loading years…'
                      : qbsPlanYearOptions.length === 0
                        ? 'No quantified plans'
                        : 'Select year'}
                </option>
                {qbsPlanYearOptions.map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1 tw-block">Show Values</Form.Label>
              <div className="tw-inline-flex" role="group" aria-label="Show values">
                <input
                  type="checkbox"
                  className="tw-sr-only"
                  id="qv-toggle-actual"
                  checked={filters.showActual}
                  onChange={(e) => setFilters(prev => ({ ...prev, showActual: e.target.checked }))}
                />
                <label
                  className={`tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-primary tw-rounded-l tw-cursor-pointer ${filters.showActual ? 'tw-bg-primary tw-text-white' : 'tw-bg-surface tw-text-primary'}`}
                  htmlFor="qv-toggle-actual"
                >
                  Actual
                </label>
                <input
                  type="checkbox"
                  className="tw-sr-only"
                  id="qv-toggle-planned"
                  checked={filters.showPlanned}
                  onChange={(e) => setFilters(prev => ({ ...prev, showPlanned: e.target.checked }))}
                />
                <label
                  className={`tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-l-0 tw-border-primary tw-rounded-r tw-cursor-pointer ${filters.showPlanned ? 'tw-bg-primary tw-text-white' : 'tw-bg-surface tw-text-primary'}`}
                  htmlFor="qv-toggle-planned"
                >
                  Planned
                </label>
              </div>
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>

        {summary && (
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="bi bi-graph-up-arrow me-2 text-primary"></i>
                Annual Target Progress
              </h6>
              <InfoTooltip
                content={
                  <>
                    Projected % of Target = (Total Actual + Total Planned) ÷ Annual Target.
                    <br />
                    Reflects the current Show Values selection ({viewModeLabel}).
                  </>
                }
                id="qv-summary-tooltip"
                placement="left"
              >
                <span className="text-muted" style={{ fontSize: '0.85rem', cursor: 'help' }}>
                  <i className="bi bi-info-circle"></i>
                </span>
              </InfoTooltip>
            </div>
            <div className="card-body">
              <Row className="g-3 text-center">
                <Col md={2} sm={4} xs={6}>
                  <div className="text-muted small text-uppercase">Annual Target</div>
                  <div className="fs-5 fw-semibold">
                    {summary.annualTarget !== null ? formatDollar(summary.annualTarget, true) : 'Not Set'}
                  </div>
                </Col>
                <Col md={2} sm={4} xs={6}>
                  <div className="text-muted small text-uppercase">Total Actual</div>
                  <div className="fs-5 fw-semibold text-success">{formatDollar(summary.totalActualValue, true)}</div>
                </Col>
                <Col md={2} sm={4} xs={6}>
                  <div className="text-muted small text-uppercase">Total Planned</div>
                  <div className="fs-5 fw-semibold text-primary">{formatDollar(summary.totalPlannedValue, true)}</div>
                </Col>
                <Col md={2} sm={4} xs={6}>
                  <div className="text-muted small text-uppercase">Total Actual + Planned</div>
                  <div className="fs-5 fw-semibold">{formatDollar(summary.totalValue, true)}</div>
                </Col>
                <Col md={4} sm={12} xs={12}>
                  <div className="text-muted small text-uppercase">Projected % of Target</div>
                  <div
                    className={`fs-4 fw-bold ${
                      summary.projectedPercentOfTarget === null
                        ? 'text-muted'
                        : summary.projectedPercentOfTarget >= 100
                          ? 'text-success'
                          : summary.projectedPercentOfTarget >= 80
                            ? 'text-warning'
                            : 'text-danger'
                    }`}
                  >
                    {summary.projectedPercentOfTarget !== null
                      ? `${summary.projectedPercentOfTarget.toFixed(1)}%`
                      : '—'}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
        <Card style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
            <h6 className="tw-mb-0">
              Quantified Value — <span className="tw-text-content-muted tw-font-normal" style={{ fontSize: '0.9em' }}>{viewModeLabel}</span>
              {activities.length > 0 && (
                <span className="tw-text-content-muted tw-ml-2" style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                  ({activities.length} {activities.length === 1 ? 'activity' : 'activities'})
                </span>
              )}
            </h6>
            <div className="tw-flex tw-items-center tw-gap-2">
              {loadingActivities && (
                <span className="tw-text-content-muted" style={{ fontSize: '0.875rem' }}>Loading activities...</span>
              )}
              {activityRows.length > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleExport}
                  disabled={!filters.serviceArea || !filters.quantifiedYear || loadingExport}
                >
                  <i className="bi bi-download tw-mr-1"></i>
                  {loadingExport ? 'Exporting...' : 'Export Report'}
                </Button>
              )}
            </div>
          </Card.Header>

          {/* No viewport-relative maxHeight here - it's what made the horizontal scrollbar land
              off-screen (see ReportPreviewTable.tsx for the same fix on the shared table). This
              box now sizes purely from flex, so its scrollbar always sits at the real edge of the
              visible table area. */}
          <Card.Body className="tw-p-0" style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ overflow: 'auto', minHeight: 0, minWidth: 0, height: '100%' }}>
              {activityRows.length === 0 && !loadingActivities ? (
                <div className="tw-text-center tw-py-5 tw-text-content-muted">
                  {!filters.serviceArea || !filters.quantifiedYear
                    ? 'Please select Service Area and Quantified Year to view activities'
                    : 'No activities found for the selected filters. Run the report to load value data.'}
                </div>
              ) : (
                <Table striped bordered hover style={{ marginBottom: 0, width: 'max-content', minWidth: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                    <tr>
                      <th style={{ maxWidth: '20%', width: '20%', backgroundColor: '#f8f9fa', position: 'sticky', left: 0, zIndex: 12, boxShadow: '2px 0 4px rgba(0,0,0,0.1)' }} title="Activity">
                        Activity
                      </th>
                      {monthHeaders.length > 0 ? (
                        monthHeaders.map((month) => (
                          <th key={month.label} style={{ minWidth: '100px', textAlign: 'center', backgroundColor: '#f8f9fa' }} title={month.dateRange ? `${month.label} — ${month.dateRange}` : month.label}>
                            <div>{month.label}</div>
                            {month.dateRange && (
                              <div style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#6c757d' }}>{month.dateRange}</div>
                            )}
                          </th>
                        ))
                      ) : (
                        Array.from({ length: 12 }, (_, i) => (
                          <th key={`M${i + 1}`} style={{ minWidth: '100px', textAlign: 'center', backgroundColor: '#f8f9fa' }} title={`M${i + 1}`}>{`M${i + 1}`}</th>
                        ))
                      )}
                      {(effectiveViewMode === 'actual' || effectiveViewMode === 'both') && (
                        <th style={{ minWidth: '130px', textAlign: 'center', backgroundColor: '#f8f9fa' }} title="Actual Value to date ($)">Actual Value to date ($)</th>
                      )}
                      {(effectiveViewMode === 'planned' || effectiveViewMode === 'both') && (
                        <th style={{ minWidth: '130px', textAlign: 'center', backgroundColor: '#f8f9fa' }} title="Planned Value to date ($)">Planned Value to date ($)</th>
                      )}
                      {effectiveViewMode === 'both' && (
                        <th style={{ minWidth: '130px', textAlign: 'center', backgroundColor: '#f8f9fa' }} title="Total Value to date ($)">Total Value to date ($)</th>
                      )}
                      <th style={{ minWidth: '140px', textAlign: 'center', backgroundColor: '#f8f9fa' }} title="Total Accomplished">Total Accomplished</th>
                      <th style={{ minWidth: '80px', textAlign: 'center', backgroundColor: '#f8f9fa' }} title="Units">Units</th>
                      <th style={{ minWidth: '150px', textAlign: 'center', backgroundColor: '#f8f9fa' }} title="Total Planned Accomplish">Total Planned Accomplish</th>
                      <th style={{ minWidth: '80px', textAlign: 'center', backgroundColor: '#f8f9fa' }} title="Units">Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityRows.map((row, index) => (
                      <tr key={row.activity.id || index}>
                        <td style={{ padding: '8px 12px', position: 'sticky', left: 0, zIndex: 9, backgroundColor: 'white', boxShadow: '2px 0 4px rgba(0,0,0,0.1)', maxWidth: '20%', width: '20%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {truncateActivityName(
                            row.activity.activityNumber && row.activity.activityName
                              ? `(#${row.activity.activityNumber}) ${row.activity.activityName}`
                              : row.activity.activityName || row.activity.activityNumber || 'Unknown Activity'
                          )}
                        </td>
                        {row.monthValues.map((cell, monthIndex) => {
                          const value = displayedMonthValue(cell);
                          const hasValue = value !== 0;
                          const label = monthHeaders[monthIndex]?.label || `M${monthIndex + 1}`;
                          return (
                            <td
                              key={monthIndex}
                              style={{
                                textAlign: 'right',
                                padding: '8px 12px',
                                cursor: hasValue ? 'pointer' : 'default',
                                color: hasValue ? '#0d6efd' : undefined,
                                textDecoration: hasValue ? 'underline' : undefined
                              }}
                              title={hasValue ? `Click to see tasks for ${label}` : undefined}
                              onClick={hasValue ? () => handleDrillThrough(row, monthIndex, label) : undefined}
                            >
                              <InfoTooltip
                                content={renderMonthTooltip(cell)}
                                id={`qv-tooltip-${row.activity.id}-${monthIndex}`}
                                placement="top"
                              >
                                {formatDollar(value)}
                              </InfoTooltip>
                            </td>
                          );
                        })}
                        {(effectiveViewMode === 'actual' || effectiveViewMode === 'both') && (
                          <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatDollar(row.actualValueToDate)}</td>
                        )}
                        {(effectiveViewMode === 'planned' || effectiveViewMode === 'both') && (
                          <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatDollar(row.plannedValueToDate)}</td>
                        )}
                        {effectiveViewMode === 'both' && (
                          <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatDollar(row.totalValueToDate)}</td>
                        )}
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatQuantity(row.totalAccomplishment)}</td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>{row.unitOfMeasure || row.activity.unitOfMeasure || '-'}</td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatQuantity(row.totalPlannedAccomplishment)}</td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>{row.unitOfMeasure || row.activity.unitOfMeasure || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      <Modal show={drillThrough.show} onHide={closeDrillThrough} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>
            Tasks — {drillThrough.activityLabel}
            <div className="tw-text-content-muted tw-font-normal" style={{ fontSize: '0.85rem' }}>
              {drillThrough.monthLabel} · {viewModeLabel}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {drillThrough.loading && (
            <div className="tw-flex tw-justify-center tw-py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          {drillThrough.error && <Alert variant="danger">{drillThrough.error}</Alert>}
          {!drillThrough.loading && !drillThrough.error && drillThrough.tasks.length === 0 && (
            <p className="tw-text-content-muted tw-text-center tw-py-3">No tasks found for this period.</p>
          )}
          {!drillThrough.loading && !drillThrough.error && drillThrough.tasks.length > 0 && (
            <Table striped bordered hover size="sm" style={{ fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th title="Task #">Task #</th>
                  <th title="Name">Name</th>
                  <th title="Status">Status</th>
                  <th style={{ textAlign: 'right' }} title="Value ($)">Value ($)</th>
                  <th style={{ textAlign: 'right' }} title="Accomplishment">Accomplishment</th>
                  <th title="Date">Date</th>
                </tr>
              </thead>
              <tbody>
                {drillThrough.tasks.map((task) => (
                  <tr key={task.taskId}>
                    <td>{task.taskNumber || task.taskId}</td>
                    <td>{task.taskName || '—'}</td>
                    <td>
                      <Badge bg={STATUS_VARIANT[task.status] || 'secondary'} style={{ fontSize: '0.75rem' }}>
                        {STATUS_LABELS[task.status] || task.status}
                      </Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {task.value !== 0 ? '$' + Math.round(task.value).toLocaleString('en-US') : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {task.accomplishment != null && task.accomplishment !== 0
                        ? task.accomplishment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-'}
                    </td>
                    <td>{task.date || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                  <td colSpan={3}>Total</td>
                  <td style={{ textAlign: 'right' }}>
                    {'$' + Math.round(drillThrough.tasks.reduce((s, t) => s + t.value, 0)).toLocaleString('en-US')}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {(() => {
                      const total = drillThrough.tasks.reduce((s, t) => s + (t.accomplishment ?? 0), 0);
                      return total !== 0
                        ? total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-';
                    })()}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={closeDrillThrough}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Toast message={toastMessage} type="success" show={showToast} onClose={() => setShowToast(false)} />
    </div>
  );
};

export default QBSQuantifiedValueReportView;
